import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Link,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { Login as LoginIcon, Handshake as HandshakeIcon, Dashboard as DashboardIcon } from '@mui/icons-material';
import EncycdataLogo from '@components/layout/EncycdataLogo';
import { useAuthStore } from '@store/authStore';
import { UserRole } from '@types/index';

interface SponsorPublicShellProps {
  children: React.ReactNode;
  hero?: React.ReactNode;
}

const SponsorPublicShell: React.FC<SponsorPublicShellProps> = ({ children, hero }) => {
  const { isAuthenticated, user } = useAuthStore();
  const isSponsor = isAuthenticated && user?.role === UserRole.SPONSOR;

  return (
  <Box sx={{ minHeight: '100vh', bgcolor: '#0b1220' }}>
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: 'rgba(11, 18, 32, 0.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Toolbar sx={{ gap: 2 }}>
        <EncycdataLogo height={36} />
        <Stack direction="row" spacing={2} sx={{ ml: 2, display: { xs: 'none', md: 'flex' } }}>
          <Link component={RouterLink} to="/sponsors" underline="none" sx={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.9rem' }}>
            Overview
          </Link>
          <Link component={RouterLink} to="/sponsors/opportunities" underline="none" sx={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.9rem' }}>
            Opportunities
          </Link>
          <Link component={RouterLink} to="/register/sponsor" underline="none" sx={{ color: '#fbbf24', fontWeight: 700, fontSize: '0.9rem' }}>
            Register
          </Link>
          <Link component={RouterLink} to="/" underline="none" sx={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.9rem' }}>
            EncycGames Home
          </Link>
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        {isSponsor ? (
          <Button
            component={RouterLink}
            to="/sponsor/dashboard"
            startIcon={<DashboardIcon />}
            sx={{ color: '#e2e8f0', fontWeight: 700, textTransform: 'none', mr: 1 }}
          >
            Dashboard
          </Button>
        ) : (
          <Button
            component={RouterLink}
            to="/login"
            startIcon={<LoginIcon />}
            sx={{ color: '#e2e8f0', fontWeight: 700, textTransform: 'none', mr: 1 }}
          >
            Login
          </Button>
        )}
        <Button
          component={RouterLink}
          to="/sponsors#express-interest"
          variant="contained"
          startIcon={<HandshakeIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
            boxShadow: '0 8px 24px rgba(245, 158, 11, 0.35)',
          }}
        >
          Partner with us
        </Button>
      </Toolbar>
    </AppBar>

    {hero}

    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      {children}
    </Container>

    <Box sx={{ py: 4, borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: '#070d18' }}>
      <Container maxWidth="lg">
        <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center' }}>
          EncycGames Partnerships · Global platform · India-first rollout · Multi-tier sponsorship matched to your budget
        </Typography>
      </Container>
    </Box>
  </Box>
  );
};

export default SponsorPublicShell;
